import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  CheckDuplicateStockCodeQuery,
  CreateStockBody,
  InventoryItemApi,
  StockApi,
  UpdateStockBody,
  UpdateStockCustomerBody,
  UpdateStockSupplierBody,
} from 'api';

export const initStock = createAsyncThunk(
  'stock/initStock',
  async (_, { rejectWithValue }) => {
    try {
      const response = await StockApi.initStock();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const selectStock = createAsyncThunk(
  'stock/selectStock',
  async (_, { rejectWithValue }) => {
    try {
      const response = await StockApi.select();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findStock = createAsyncThunk(
  'stock/findStock',
  async (_, { rejectWithValue }) => {
    try {
      const response = await StockApi.findStock();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findInventoryItemWithProduct = createAsyncThunk(
  'stock/findInventoryItemWithProduct',
  async (data: any, { rejectWithValue }) => {
    try {
      const { id, ...option } = data;

      const response = await StockApi.findInventoryItemWithProductID(
        id,
        option
      );

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findInventoryItemInfo = createAsyncThunk(
  'stock/findInventoryItemInfo',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await StockApi.findInventoryItemInfo(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findInventoryItemInfoWithUnit = createAsyncThunk(
  'stock/findInventoryItemInfoWithUnit',
  async (body: any, { rejectWithValue }) => {
    try {
      const response = await StockApi.findInventoryItemInfoWithUnit(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkDuplicateStockCode = createAsyncThunk(
  'stock/checkDuplicateStockCode',
  async (query: CheckDuplicateStockCodeQuery, { rejectWithValue }) => {
    try {
      const response = await StockApi.checkDuplicate(query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const filterStock = createAsyncThunk(
  'stock/filterStock',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await StockApi.filter(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const generateStockCode = createAsyncThunk(
  'stock/generateStockCode',
  async (_, { rejectWithValue }) => {
    try {
      const response = await StockApi.generateCode();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createStock = createAsyncThunk(
  'stock/createStock',
  async (body: CreateStockBody, { rejectWithValue }) => {
    try {
      const response = await StockApi.createStock(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateStock = createAsyncThunk(
  'stock/updateStock',
  async (param: { id: string; body: UpdateStockBody }, { rejectWithValue }) => {
    const { id, body } = param;

    try {
      const response = await StockApi.updateStock(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const checkCanCreateStock = createAsyncThunk(
  'stock/checkCanCreateStock',
  async (query: any, { rejectWithValue }) => {
    try {
      const response = await StockApi.checkCanCreateStock(query);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findInventoryHistory = createAsyncThunk(
  'stock/findInventoryHistory',
  async (param: { id: string; option: any }, { rejectWithValue }) => {
    const { id, option } = param;

    try {
      const response = await StockApi.findInventoryHistory(id, option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findStockForCreateProduct = createAsyncThunk(
  'stock/findStockForCreateProduct',
  async (_, { rejectWithValue }) => {
    try {
      const response = await StockApi.findStockForCreateProduct();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findStockForInheritStock = createAsyncThunk(
  'stock/findStockForInheritStock',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await StockApi.findStockForInheritStock(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findOneWithRefInfoStock = createAsyncThunk(
  'stock/findOneWithRefInfoStock',
  async (option: any, { rejectWithValue }) => {
    try {
      const response = await StockApi.findOneWithRefInfo(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteStock = createAsyncThunk(
  'stock/deleteStock',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await StockApi.deleteStock(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findParentsStock = createAsyncThunk(
  'stock/findParentsStock',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await StockApi.findParentsStock(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findInventoryItemWithUnit = createAsyncThunk(
  'stock/findInventoryItemWithUnit',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await StockApi.findInventoryItemWithUnit(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntityDocumentSubCategoryForStock = createAsyncThunk(
  'stock/findEntityDocumentSubCategoryForStock',
  async (_, { rejectWithValue }) => {
    try {
      const response = await StockApi.findEntityDocumentSubCategoryForStock();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findInventoryItemWithProductID = createAsyncThunk(
  'stock/findInventoryItemWithProductID',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await InventoryItemApi.findInventoryItemWithProduct(id);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findDetailCommittedForInventoryItem = createAsyncThunk(
  'stock/findDetailCommittedForInventoryItem',
  async (
    param: { id: string; body: any; option: any },
    { rejectWithValue }
  ) => {
    const { id, body, option } = param;

    try {
      const response = await StockApi.findDetailCommittedForInventoryItem({
        id,
        body,
        option,
      });

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findDetailIncomingForInventoryItem = createAsyncThunk(
  'stock/findDetailIncomingForInventoryItem',
  async (
    param: { id: string; body: any; option: any },
    { rejectWithValue }
  ) => {
    const { id, body, option } = param;

    try {
      const response = await StockApi.findDetailIncommingForInventoryItem({
        id,
        body,
        option,
      });

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findDetailOnwayForInventoryItem = createAsyncThunk(
  'stock/findDetailOnwayForInventoryItem',
  async (
    param: { id: string; body: any; option: any },
    { rejectWithValue }
  ) => {
    const { id, body, option } = param;

    try {
      const response = await StockApi.findDetailOnwayForInventoryItem({
        id,
        body,
        option,
      });

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const quicklyInitStockForChain = createAsyncThunk(
  'stock/quicklyInitStockForChain',
  async (_, { rejectWithValue }) => {
    try {
      const response = await StockApi.quicklyInitStockForChain();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateStockType = createAsyncThunk(
  'stock/updateStockType',
  async (param: { id: string; body: any }, { rejectWithValue }) => {
    const { id, body } = param;

    try {
      const response = await StockApi.updateStockType(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateStockSupplier = createAsyncThunk(
  'stock/updateStockSupplier',
  async (
    param: { id: string; body: UpdateStockSupplierBody },
    { rejectWithValue }
  ) => {
    const { id, body } = param;

    try {
      const response = await StockApi.updateStockSupplier(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateStockCustomer = createAsyncThunk(
  'stock/updateStockCustomer',
  async (
    param: { id: string; body: UpdateStockCustomerBody },
    { rejectWithValue }
  ) => {
    const { id, body } = param;

    try {
      const response = await StockApi.updateStockCustomer(id, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEntitiesForCreateStock = createAsyncThunk(
  'stock/findEntitiesForCreateStock',
  async (_, { rejectWithValue }) => {
    try {
      const response = await StockApi.findEntityForCreate();

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const findEmployeeForStock = createAsyncThunk(
  'stock/findEmployeeForStock',
  async (param: any, { rejectWithValue }) => {
    try {
      const response = await StockApi.findEmployeeForStock(param);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

interface State {
  tableList: Array<any>;
  list: Array<any>;
  reloadFlag: boolean;
  stockList: Array<any>;
  entityDocumentSubCategoryList: Array<any>;
  isLoaded: boolean;
  isLoadedEntityDocumentSubCategory: boolean;
  isLoadedStockList: boolean;
}

const initialState: State = {
  tableList: [],
  stockList: [],
  entityDocumentSubCategoryList: [],
  isLoadedEntityDocumentSubCategory: false,
  list: [],
  reloadFlag: false,
  isLoaded: false,
  isLoadedStockList: false,
};

const stockSlice = createSlice({
  name: 'stock',
  initialState,
  reducers: {
    reloadStock(state) {
      state.reloadFlag = !state.reloadFlag;
    },
  },
  extraReducers: builder => {
    builder.addCase(
      findEntityDocumentSubCategoryForStock.fulfilled,
      (state, action) => {
        const { data } = action.payload;

        const newData = data.map(item => {
          return {
            ...item,
            key: item.id,
            value: item.id,
          };
        });

        state.entityDocumentSubCategoryList = newData;
        state.isLoadedEntityDocumentSubCategory = true;
      }
    );

    builder.addCase(selectStock.fulfilled, (state, action) => {
      const { data } = action.payload;

      const formatData = data.map(item => {
        return {
          ...item,
          value: item.entityID,
        };
      });

      state.stockList = formatData;
      state.isLoadedStockList = true;
    });
  },
});

const { actions, reducer: stockReducer } = stockSlice;

export const { reloadStock } = actions;

export default stockReducer;
